import { CxfEvents, IGameEvent, ILoginData } from '@goodgamestudios/cxf-events'
import { createDialog } from '../dialog'
import { globalState } from '../globalState'
import { trackOpenAction } from '../track'
import { CanvasAppEvent, CanvasEventPostMessage, CxfEventHandlers, OpenIGSPayload, WebshopEvents } from '../types'
import { IPushMessageData } from '../types/common'
import { ICXF } from '../types/cxf'
import {
    createProductIdsForPlatform,
    createSessionId,
    detectPlatform,
    log,
    logError,
    transformPaymentContractIdsToNativePriceEvent,
    tryCatch,
} from '../utils'
import { createIframeUrl } from '../utils/url'
import { createPushHandlers } from './pushHandlers'

let isWindowMessageListenerAttached = false

const eventListener = (event: CanvasEventPostMessage) => {
    log(`Canvas event "${event.eventType}" received:`, event)

    switch (event.eventType) {
        case CanvasAppEvent.CLOSE: {
            window.postMessage(
                {
                    name: WebshopEvents.CXF_DIALOG_CLOSE,
                },
                '*'
            )
            break
        }
        case CanvasAppEvent.NATIVE_CATALOG_REQUEST: {
            if (event?.paymentContractIds?.length > 0) {
                if (!isWindowMessageListenerAttached) {
                    window.addEventListener('message', ({ data }: MessageEvent) => {
                        if (
                            data?.eventName === WebshopEvents.LEMONSTAND_CATEGORY_UPDATE &&
                            data?.data?.action === 'nativePriceUpdate'
                        ) {
                            // Native client provided back to us native prices for payment contracts
                            log(`${WebshopEvents.LEMONSTAND_CATEGORY_UPDATE} event`, data.data)
                            if (!data.data.data) return
                            const nativeCatalog: Record<string, { amount: number; currency: string }> = {}
                            for (const key in data.data.data) {
                                const { priceString, ...rest } = data.data.data[key]
                                nativeCatalog[key] = rest
                            }
                            log('nativeCatalog:', nativeCatalog)
                            window.CanvasAgent?.instance?.setNativeCatalog(nativeCatalog)
                        }
                    })
                    isWindowMessageListenerAttached = true
                }
                // Send payment contract IDs to native client
                const msg = transformPaymentContractIdsToNativePriceEvent(event.paymentContractIds)
                window.postMessage(msg, '*')
                log('Event `lemonstand.nativePrice` sent')
            }
            break
        }
        case CanvasAppEvent.NATIVE_CHECKOUT_REQUEST: {
            const { paymentContract, sessionId, offerId, distributionPlatform } = event
            const { zoneId } = globalState.getStore()
            window.postMessage(
                {
                    eventName: 'lemonstand.nativePay',
                    eventType: 'bubble',
                    nativePayInfo: {
                        pkg: {
                            price: paymentContract?.price?.amount,
                            productIdMS: '',
                            productIds: createProductIdsForPlatform(distributionPlatform, paymentContract?.id),
                            tier: Number(paymentContract?.id?.split('_')?.at(-1)),
                        },
                        meta: {
                            criteria: window.CanvasAgent?.instance?.getState()?.appConfig?.canvasAppId,
                            lemonstand: true,
                            offerId,
                            sid: sessionId,
                            type: 'canvas', // IMPORTANT: type must be 'canvas'
                            zoneId,
                            successUrl: window.location.href,
                        },
                    },
                },
                '*'
            )
            log('Event `lemonstand.nativePay` sent')
            break
        }
        default: {
            break
        }
    }
}

const onOpen = (payload: OpenIGSPayload = {}): void => {
    log(CxfEvents.OpenIGS, 'payload:', payload)

    if (payload.sourceId) {
        globalState.updateStore({
            sourceId: payload.sourceId,
        })
    }

    const { isCanvas, lastPurchaseTab, gameApi } = globalState.getStore()
    if (isCanvas) {
        if (payload.page) {
            window.CanvasAgent?.instance?.navigate(payload.page)
        }
        window.CanvasAgent?.instance?.show()
        return
    }
    const sid = createSessionId()
    trackOpenAction(sid)
    const url = createIframeUrl({
        sid,
        page: lastPurchaseTab,
        ...payload,
    })
    createDialog(url)

    setTimeout(() => {
        // remove unread offer notifications counter on the IGS button while opening the IGS
        // small delay makes UX a bit better due to it happens after IGS initialization is started
        gameApi.invokeFn('setUnseenOffersCounter', 0)
    }, 1000)
}

export const subscribeToCommonCxfEvents = (cxf: ICXF): void => {
    const pushHandlers = createPushHandlers()

    cxf.on(CxfEvents.OpenIGS, tryCatch(onOpen))
    cxf.on(
        CxfEvents.Push,
        tryCatch(({ id, payload }: IPushMessageData) => {
            log(CxfEvents.Push, id, payload)
            const handler = pushHandlers[id]
            handler && handler(payload)
        })
    )
}

const createLeadingEdgeThrottle = <T extends (...args: any[]) => void>(fn: T, delay: number): T => {
    let lastCall = 0
    return ((...args: Parameters<T>) => {
        const now = Date.now()
        if (now - lastCall >= delay) {
            lastCall = now
            fn(...args)
        }
    }) as T
}

export const subscribeToGameEvents = (cxf: ICXF): void => {
    const throttledLoginHandler = createLeadingEdgeThrottle((e: ILoginData) => {
        const { gameEvents: eventsInLogin, language, token, zoneId } = e
        globalState.updateStore({
            isCanvas: true,
        })
        const { gameEvents, isCanvas } = globalState.getStore()
        // eslint-disable-next-line unicorn/consistent-function-scoping
        const eventNotExistPredicateGenerator = (existingEvents: IGameEvent[]) => (event: IGameEvent) =>
            !existingEvents.map((event_) => event_.type).includes(event.type)
        globalState.updateStore({
            ...e,
            gameEvents: [...gameEvents.filter(eventNotExistPredicateGenerator(eventsInLogin)), ...eventsInLogin],
        })
        log(CxfEvents.Login, globalState.getStore())
        if (!isCanvas) {
            globalState.fetchUnreadOfferNotificationsCount()
            return
        }
        // Logic for Canvas
        if (typeof window.CanvasAgent?.create === 'function') {
            const { CANVAS_APP_ID_BY_GAME } = globalState.getConfig()
            const platform = detectPlatform()
            const canvasApps = (CANVAS_APP_ID_BY_GAME as Record<string, string | Record<string, string>>)[cxf.gameId]
            const canvasAppId =
                typeof canvasApps === 'string' ? canvasApps : (canvasApps as Record<string, string>)[platform]

            if (!canvasAppId) {
                logError('Canvas app id not found for platform', { platform, canvasApps })
                return
            }
            // Destroy existing Canvas instance
            window.CanvasAgent?.instance?.destroy()

            window.CanvasAgent.create({
                authClientId: 'lemonstand',
                canvasAppId,
                eventListener,
                locale: language,
                startupTarget: 'customized',
                token,
            })
                .then((res) => {
                    window.CanvasAgent.instance = res
                    log(CxfEvents.Login, 'Canvas created')
                })
                .catch((error) => {
                    logError('Cannot create instance of Canvas', error)
                })
        }
    }, 3000)

    cxf.on(CxfEvents.Login, throttledLoginHandler)

    cxf.on(CxfEvents.GameEventUpdate, (e: IGameEvent[]) => {
        log(CxfEvents.GameEventUpdate, e)
        const { gameEvents, isCanvas } = globalState.getStore()
        const updatedEventTypes = new Set(e.map((event) => event.type))
        globalState.updateStore({
            gameEvents: [...gameEvents.filter((event) => !updatedEventTypes.has(event.type)), ...e],
        })
        if (isCanvas) {
            window.CanvasAgent?.instance?.refresh()
        }
        log(CxfEvents.GameEventUpdate, 'reducer', globalState.getStore())
    })

    cxf.on(CxfEvents.GameEventAdd, (e: IGameEvent) => {
        log(CxfEvents.GameEventAdd, e)
        const { gameEvents, isCanvas } = globalState.getStore()
        globalState.updateStore({
            gameEvents: [...gameEvents, e],
        })
        if (isCanvas) {
            window.CanvasAgent?.instance?.refresh()
        }
        log(CxfEvents.GameEventAdd, 'reducer', globalState.getStore())
    })

    cxf.on(CxfEvents.GameEventRemove, (e: number) => {
        log(CxfEvents.GameEventRemove, e)
        const { gameEvents, isCanvas } = globalState.getStore()
        globalState.updateStore({
            gameEvents: gameEvents.filter(({ type }) => type !== e),
        })
        if (isCanvas) {
            window.CanvasAgent?.instance?.refresh()
        }
        log(CxfEvents.GameEventRemove, 'reducer', globalState.getStore())
    })

    cxf.on(CxfEvents.LevelChanged, (level: number) => {
        log(CxfEvents.LevelChanged, level)
        const { isCanvas } = globalState.getStore()
        globalState.updateStore({
            level,
        })
        log(CxfEvents.LevelChanged, 'reducer', globalState.getStore())
        if (isCanvas) {
            window.CanvasAgent?.instance?.refresh()
        } else {
            globalState.fetchUnreadOfferNotificationsCount()
        }
    })

    cxf.on(CxfEvents.LegendLevelChanged, (legendLevel: number) => {
        log(CxfEvents.LegendLevelChanged, `legendLevel: ${legendLevel}`)
        globalState.updateStore({
            legendLevel,
        })
        const store = globalState.getStore()
        log(CxfEvents.LegendLevelChanged, 'reducer', store)
        globalState.fetchUnreadOfferNotificationsCount()
    })
}

/*
    In E4K this event is used to control the subscriptions tab
    This is deprecated, as specific config values/tab visibility can be controlled using Lemonstands `config` query param
*/
export const subscribeToNativeSubscriptionEnable = (cxf: ICXF): void => {
    cxf.on(WebshopEvents.CXF_NATIVE_SUBSCRIPTION_ENABLE, (enabled: 0 | 1) => {
        globalState.updateStore({
            subscriptionDisabled: enabled === 0,
            customizationSuffix: enabled === 0 ? 'no-subscription' : '',
        })
        globalState.preResolveConfig()
    })
}

/*
    The config file suffix (e.g. "em.json" + "mobile" -> "em-mobile.json") can be set through an event
    Used by MS and BFW integration pages
*/
export const subscribeToCustomizationSuffix = (cxf: ICXF): void => {
    cxf.on(WebshopEvents.CXF_SET_CUSTOMIZATION_SUFFIX, (suffix: string) => {
        const { customizationSuffix } = globalState.getStore()
        if (suffix === customizationSuffix) {
            return
        }
        globalState.updateStore({
            customizationSuffix: suffix || '',
        })
        globalState.preResolveConfig()
    })
}

/*
    Information about whether ads are enabled or not is retrieved externally from `cxf-ad-banners`
    and stored to be passed to IGS
*/
export const subscribeToAdStatus = (cxf: ICXF): void => {
    const config = globalState.getConfig()
    cxf.on(config.CXF_AD_STATUS, ({ areBannersAvailable, bannersDetails }: Record<string, any>) => {
        globalState.updateStore({
            adStatus: {
                areBannersAvailable,
                bannersDetails,
            },
        })
    })
}

/*
    Use a dedicated config variant on temp servers
*/
export const subscribeToTempServerContext = (cxf: ICXF): void => {
    cxf.on(WebshopEvents.CXF_JOIN_TEMP_SERVER, (isTemp: 0 | 1) => {
        const store = globalState.getStore()
        globalState.updateStore({
            isTempServer: Boolean(isTemp),
        })

        // ensure suffix is not already set by the environment, e.g. BFW or MS integration
        // TODO: fully migrate client detection into `cxf-webshop`
        if (store.customizationSuffix === '' || store.customizationSuffix === 'temp') {
            globalState.updateStore({
                customizationSuffix: isTemp ? 'temp' : '',
            })
            globalState.preResolveConfig()
        }
    })
}

export const createCxfEventHandlers = (): CxfEventHandlers => {
    return [
        subscribeToCommonCxfEvents,
        subscribeToGameEvents,
        subscribeToAdStatus,
        subscribeToTempServerContext,
        subscribeToNativeSubscriptionEnable,
        subscribeToCustomizationSuffix,
    ]
}
